import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import { signalPricingCtaArrival } from '../../utils/ctaActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PromoBanner — small amber pill rendered on the landing hero when the
 * super-admin has toggled `landing_banner.enabled` ON in /api/pricing AND
 * either custom_text is set OR at least one plan has an active discount.
 * Click scrolls smoothly to #pricing.
 */
export function PromoBanner() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg?.landing_banner?.enabled) return null;

  const activeDiscounts = Object.entries(cfg.plans || {})
    .filter(([_, p]) => p.is_discounted)
    .map(([id, p]) => ({
      id,
      label: p.discount_label || `Save ${Math.round(p.discount_percent || 0)}%`,
      percent: Math.round(p.discount_percent || 0),
    }));

  const customText = (cfg.landing_banner.custom_text || '').trim();
  let bannerText = customText;
  if (!bannerText) {
    if (activeDiscounts.length === 0) return null; // nothing to advertise
    bannerText = activeDiscounts.length === 1
      ? `${activeDiscounts[0].label} — limited time on the ${activeDiscounts[0].id} plan`
      : `Promo on ${activeDiscounts.length} plans — save up to ${Math.max(...activeDiscounts.map(d => d.percent))}%`;
  }

  const ctaText = cfg.landing_banner.cta_text || 'View plans';

  const handleClick = (e) => {
    e.preventDefault();
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    signalPricingCtaArrival('pricing');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-amber-500/30 transition-all hover:scale-105 cursor-pointer"
      data-testid="hero-promo-banner"
    >
      <Sparkles className="w-3.5 h-3.5" />
      <span>{bannerText}</span>
      <span className="opacity-70">·</span>
      <span className="underline underline-offset-2">{ctaText} →</span>
    </button>
  );
}
